import axios from 'axios'
import { AxiosError, AxiosRequestConfig } from 'axios'
import { ApiError } from '@app/api/api-error'
import { clearAuthFlag, deleteUser, readAuthFlag } from '@app/store/services/local-storage.service'

import config from '../config'

/** Górna granica czasu żądania. Bez niej axios czeka bez końca: gdy backend nie
 *  odpowiada (padnięty tunel do bazy, zabity proces), obietnica nigdy się nie
 *  rozstrzyga, więc `finally` w kodzie wywołującym się nie wykonuje i widok zostaje
 *  w stanie ładowania bez błędu i bez wyjścia — do przeładowania strony.
 *
 *  Minuta, a nie kilka sekund, bo najcięższe żądanie aplikacji (pełny upsert
 *  szablonu niesie base64 wszystkich PDF-ów dokumentacji) na wolnym łączu trwa
 *  realnie długo. Żądanie wymagające więcej ustawia własny `timeout` — wartość
 *  podana przy wywołaniu nadpisuje tę domyślną. */
const REQUEST_TIMEOUT_MS = 60_000

export const httpApi = axios.create({
  baseURL: config.apiURL,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT_MS,
})

const refreshClient = axios.create({
  baseURL: config.apiURL,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT_MS,
})

const withCsrfHeader = (config: AxiosRequestConfig) => {
  config.headers = { ...config.headers, 'X-Requested-With': 'XMLHttpRequest' }
  return config
}
httpApi.interceptors.request.use(withCsrfHeader)
refreshClient.interceptors.request.use(withCsrfHeader)

export const SILENT_AUTH_ERROR = '__SILENT_AUTH__'

let inFlightRefresh: Promise<unknown> | null = null

export const refreshSession = (): Promise<unknown> => {
  if (!inFlightRefresh) {
    inFlightRefresh = refreshClient.post('auth/refresh').finally(() => {
      inFlightRefresh = null
    })
  }
  return inFlightRefresh
}

let authFailureHandled = false

export const handleAuthFailure = (): void => {
  if (authFailureHandled) return
  authFailureHandled = true

  clearAuthFlag()
  deleteUser()

  const redirect = () => {
    if (!window.location.pathname.startsWith('/auth')) {
      sessionStorage.setItem('autoLoggedOut', '1')
      window.location.assign('/auth/login')
    }
  }

  // Unieważnia sesję i zapisuje AUTH_LOGOUT. Przez `refreshClient`, bo ten nie ma interceptora
  // odpowiedzi — inaczej 401 z martwej sesji wróciłby tutaj i zapętlił się.
  refreshClient
    .post('auth/logout', undefined, { timeout: 3000 })
    .catch(() => undefined)
    .finally(redirect)
}

type RetriableConfig = AxiosRequestConfig & { _retry?: boolean }

/** Odświeża sesję i ponawia żądanie przy 401. Żądania z `skipAuthRefresh` są wyłączone —
 *  ich 401 dotyczy treści żądania, nie sesji. */
httpApi.interceptors.response.use(undefined, async (error: AxiosError) => {
  const status = error.response?.status
  const original = error.config as RetriableConfig | undefined
  const url = original?.url ?? ''

  const buildApiError = () => {
    if (!error.response) {
      return new ApiError<ApiErrorData>('common.connectionError')
    }

    if (error.response.status >= 500) {
      return new ApiError<ApiErrorData>(
        'common.serverError',
        error.response.data as ApiErrorData,
        error.response.status,
      )
    }

    return new ApiError<ApiErrorData>(
      (error.response.data as ApiErrorData | undefined)?.message || error.message,
      error.response.data as ApiErrorData,
      error.response.status,
    )
  }

  const silentAuthError = () => new ApiError<ApiErrorData>(SILENT_AUTH_ERROR)

  if (status === 401 && readAuthFlag() && original && !original.skipAuthRefresh && !url.includes('auth/refresh')) {
    if (original._retry) {
      handleAuthFailure()
      return Promise.reject(silentAuthError())
    }

    original._retry = true

    try {
      await refreshSession()
      return httpApi(original)
    } catch (refreshError) {
      if ((refreshError as AxiosError).response?.status === 401) {
        handleAuthFailure()
      }
      return Promise.reject(silentAuthError())
    }
  }

  return Promise.reject(buildApiError())
})

export interface ApiErrorData {
  message: string
}
